import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AnySchema, StringSchema, object as yupObject } from 'yup'
import { ComboboxItem, ComboboxProps } from '@node-space/ui-components'
import { FieldMaxLength } from 'constants/forms'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useIndustriesQuery } from 'hooks/queries/Customers/useIndustriesQuery'
import { useMonthlyVolumesQuery } from 'hooks/queries/Customers/useMonthlyVolumesQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { getCountryOptions } from 'utils/forms'
import { findLabelByValue, transformOptions } from 'utils/utils'
import { BASE_CUSTOMER_OPTIONS, BUSINESS_CUSTOMER_LABEL_MAPPING } from '../helpers/form'
import {
  BusinessCustomerFormData,
  BusinessCustomerFormKey,
  RiskScore,
  UseCase,
} from '../helpers/types'

type BusinessCustomerFormSchema = Record<BusinessCustomerFormKey, AnySchema>

export const useBusinessCustomerForm = (defaultValues: BusinessCustomerFormData) => {
  const { t } = useTranslation()
  const { getRequiredYupField, getYupField } = useYupFields()

  const {
    data: countriesData = [],
    isFetching: isFetchingCountries,
    isError: isErrorFetchingCountries,
  } = useCountriesQuery()

  const {
    data: industriesData = [],
    isFetching: isFetchingIndustries,
    isError: isErrorFetchingIndustries,
  } = useIndustriesQuery()

  const {
    data: monthlyVolumes,
    isFetching: isFetchingMonthlyVolumes,
    isError: isErrorFetchingMonthlyVolumes,
  } = useMonthlyVolumesQuery()

  const industryOptions = transformOptions(industriesData)

  const comboboxItems: Partial<Record<BusinessCustomerFormKey, ComboboxItem[]>> = {
    ...BASE_CUSTOMER_OPTIONS,
    countryOfIncorporation: getCountryOptions(countriesData),
    monthlyExpectedVolumes: monthlyVolumes?.map(data => ({
      leftText: data.name,
      value: data.reference,
    })),
  }

  const isComboboxLoading: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isFetchingCountries,
    monthlyExpectedVolumes: isFetchingMonthlyVolumes,
  }

  const isErrorLoadingOptions: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isErrorFetchingCountries,
    industry: isErrorFetchingIndustries,
    monthlyExpectedVolumes: isErrorFetchingMonthlyVolumes,
  }

  const optionErrorFields = Object.keys(isErrorLoadingOptions)?.filter(
    key => isErrorLoadingOptions?.[key]
  )

  const formKeys = Object.keys(defaultValues) as BusinessCustomerFormKey[]

  const getFieldSchema = (key: BusinessCustomerFormKey): StringSchema => {
    const label = t(
      BUSINESS_CUSTOMER_LABEL_MAPPING?.[key] ?? `embeddedCustomers.businessForm.${key}`
    )
    const fieldSchema = getRequiredYupField({ label })
    switch (key) {
      case 'businessDescription':
        return fieldSchema?.max(FieldMaxLength.LONG, t('formFields.errors.fieldTooLong'))
      case 'externalReference':
        return getYupField({ label })
      default:
        return fieldSchema
    }
  }

  const formSchema = yupObject(
    formKeys?.reduce((acc, key) => {
      acc[key] = getFieldSchema(key)
      return acc
    }, {} as BusinessCustomerFormSchema)
  )
  const form = useForm<BusinessCustomerFormData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const { handleSubmit, setValue, setError, control } = form
  const formValues = useWatch({ control })
  const hasErrors = !!Object.keys(form?.formState?.errors)?.length

  const { setInputField, setComboboxField, setSelectField } =
    useFormFields<BusinessCustomerFormKey>(formSchema, form, formValues)

  const getFormField = (key: BusinessCustomerFormKey) => {
    if (Object.keys(comboboxItems)?.includes(key)) {
      const comboboxField: ComboboxProps = {
        ...setComboboxField({ key, items: comboboxItems?.[key] }),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: defaultValues?.[key],
      }
      return comboboxField
    }
    if (key === 'industry') {
      return {
        ...setSelectField({ key, options: [] }),
        loading: isFetchingIndustries,
        options: industryOptions,
      }
    }
    return {
      ...setInputField({ key }),
      onChange: event => setValue(key, event?.target?.value, { shouldValidate: true }),
    }
  }

  const formFields = formKeys?.reduce(
    (acc, key) => {
      acc[key] = getFormField(key)
      return acc
    },
    {} as Record<BusinessCustomerFormKey, any>
  )

  const getComboboxLabel = <LabelType extends string>(key: BusinessCustomerFormKey) => {
    return comboboxItems?.[key]?.find(({ value }) => value === formValues?.[key])
      ?.leftText as LabelType
  }

  const displayData: Partial<BusinessCustomerFormData> = {
    industry: findLabelByValue(formValues?.industry, industryOptions),
    countryOfIncorporation: getComboboxLabel('countryOfIncorporation'),
    monthlyExpectedVolumes: getComboboxLabel('monthlyExpectedVolumes'),
    riskScore: getComboboxLabel<RiskScore>('riskScore'),
    useCase: getComboboxLabel<UseCase>('useCase'),
  }

  useEffect(() => {
    if (optionErrorFields?.length) {
      optionErrorFields?.forEach(key => {
        setError(key as BusinessCustomerFormKey, {
          type: 'disabled',
          message: t('formFields.errors.loadingOptions'),
        })
      })
    }
  }, [optionErrorFields?.length])

  return { formFields, displayData, hasErrors, handleSubmit }
}
